// Idempotency keys (05 §1.4). Clients generate one UUIDv7 per user intent and
// reuse it on retry; the server stores the response with the state change.

/** UUIDv7: time-ordered, so keys index well and sort by creation. */
// Ids made in the same millisecond count up (RFC 9562 method 1, a 12-bit
// counter): "id order is time order" was only true across milliseconds, and
// two chat messages sent in one came back swapped.
let lastMs = -1
let seq = 0

export function uuidv7(now = Date.now()): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  if (now === lastMs && seq < 0xfff) seq++
  else seq = (bytes[6]! << 4 | bytes[7]! >> 4) & 0x7ff
  lastMs = now
  const ts = BigInt(now)
  for (let i = 0; i < 6; i++) bytes[i] = Number((ts >> BigInt(8 * (5 - i))) & 0xffn)
  bytes[6] = 0x70 | (seq >> 8)
  bytes[7] = seq & 0xff
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isValidKey(key: string | null | undefined): key is string {
  return typeof key === 'string' && UUID_RE.test(key)
}

/** JSON with sorted keys, so the same body always hashes the same. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** The `request_hash` column: a key reused with a different body is a 422. */
export function requestHash(method: string, route: string, body: unknown): Promise<string> {
  return sha256Hex(`${method.toUpperCase()} ${route}\n${canonicalJson(body ?? null)}`)
}

/**
 * Dedupe key for an outbox side effect: the same logical effect always maps to
 * the same key, so a redelivery is recognised by the target or by our probe.
 */
export function dedupeKey(kind: string, ...parts: Array<string | number>): Promise<string> {
  return sha256Hex([kind, ...parts.map(String)].join('\u0000')).then((h) => `${kind}:${h.slice(0, 32)}`)
}

/** Hidden marker put in a Linear comment, so "did it land?" can be answered by reading. */
export function linearMarker(key: string): string {
  return `<!-- trisof:${key} -->`
}

export const IDEMPOTENCY_TTL_MS = { api: 24 * 60 * 60_000, box: 7 * 24 * 60 * 60_000 } as const
/** An `in_progress` row older than this belonged to a request that died before its batch. */
export const IDEMPOTENCY_TAKEOVER_MS = 5 * 60_000
